/**
 * `<MapHomePage/>` — the task-mode team Map home.
 *
 * Routed at `/teams/:teamId/map`, dark behind the `TARII_TASK_MODE` gate
 * (no team ever has `team_kind='tasks'` in prod while the gate is off).
 *
 * Phase 0 (#753) shipped a stub here. Phase 2 (#759, PR-1) replaces the
 * body with the real two-way editor's tree-canvas half — `<MapEditor/>` —
 * which delivers B1.3 (empty workspace), B2.1 (tree canvas, the hero),
 * B2.2 (node focused), and B8.3 (collapse). The keyboard outline editor +
 * split view + two-way sync land in #759 PR-2.
 *
 * Design ref: `docs/design/task-based-mode-exploration-2026-05-22.md`
 *   - §A7: the two-way editor + auto-layout + "beautiful is acceptance".
 *   - §A11: two cores share one shell; tasks module must not import
 *     scheduling/cycle/solver components.
 *   - B7.2: tasks team nav = Map · Members · Settings.
 *
 * Isolation: this file is the taskmap module boundary. It must not import
 * anything from the scheduling core (cycles, stations, solver, assignments).
 *
 * Brand discipline: worker-friendly + trustable copy; factual, calm;
 * no tier language; no burnout naming; no individual-performance metrics.
 */
import { useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";

import { useTeam } from "../api/useTeam";
import { MapEditor } from "./MapEditor";
import { useTaskNodes } from "./api/taskNodes";
import { useNotifications } from "./api/useNotifications";
import { HomeDashboard } from "./components/HomeDashboard";
import { MapNav } from "./components/MapNav";
import { MapMembers } from "./members/MapMembers";
import { useCurrentMember } from "./members/useCurrentMember";
import { useTaskTeamMembers } from "./members/useTaskTeamMembers";
import { MapSettingsHome } from "./settings/MapSettingsHome";
import { MapThemeProvider } from "./theme/MapThemeContext";
import { buildTree } from "./tree/buildTree";

/**
 * Phase 2 Map home = the Taskmap workspace (#759 PR-1). The editor owns its own
 * breadcrumb + team load; this thin wrapper keeps the exported component name
 * stable for the router + the #753 shell tests.
 *
 * #1043 (IA shell): the editorial `<MapNav/>` secondary nav (`Home · Taskmap`)
 * sits at the top with "Taskmap" active. It renders ONLY in editorial mode
 * (returns `null` in legacy), so the legacy Map is byte-identical.
 *
 * PR-D (#1043, founder F5, 2026-05-26): the default landing FLIPPED to Home, so
 * the Taskmap editor now mounts at `/teams/:id/map/board` (this page) and Home
 * (`<MapHomePageDashboard/>`) is the bare `/teams/:id/map`.
 *
 * `MapThemeProvider` (#1044) resolves the per-team editorial-vs-legacy look
 * once here, from the route's `:teamId`, and provides it to every Map surface
 * below via `useMapTheme()`. With `task_editorial_ui` OFF (every team today)
 * it resolves to the legacy theme, so nothing changes until the flag flips.
 */
export function MapHomePage() {
  const { teamId } = useParams<{ teamId: string }>();
  return (
    <MapThemeProvider>
      <MapNav teamId={teamId} active="taskmap" />
      <MapEditor />
    </MapThemeProvider>
  );
}

/**
 * `<MapHomePageDashboard/>` — the tasks-team overview Home (#1043, IA shell).
 *
 * PR-D (#1043, founder F5, 2026-05-26): this is now the DEFAULT landing at the
 * bare `/teams/:teamId/map`, and is the real HYBRID dashboard — a per-outcome
 * rollup strip over a filterable issues-style task list (`<HomeDashboard/>`).
 * The Taskmap editor moved to `/map/board`; `/map/home` redirects here.
 *
 * It mounts `<MapThemeProvider>` + `<MapNav active="home"/>` at the top like the
 * other Map pages so the secondary nav reads consistently. The data wiring
 * (`useTaskNodes` / `useTaskTeamMembers` → `buildTree`) lives in the
 * `HomeDashboardData` wrapper; the dashboard itself (`<HomeDashboard/>`) is a
 * pure-presentational component taking the forest + member lookup as props.
 */
export function MapHomePageDashboard() {
  const { teamId } = useParams<{ teamId: string }>();
  return (
    <MapThemeProvider>
      <MapNav teamId={teamId} active="home" />
      <HomeDashboardData teamId={teamId} />
    </MapThemeProvider>
  );
}

/**
 * Data wrapper for the Home dashboard — fetches the flat node list + member
 * roster (+ the team, for its timezone), nests the forest (`buildTree`, the one
 * place flat→nested happens), and hands the result to the pure
 * `<HomeDashboard/>`. Degrades calmly while loading (the dashboard renders a
 * skeleton). The whole tree is visible to every member (§A8), so this is the
 * same read for leads + members.
 *
 * Round-3 (item 2): the dashboard now hosts a project-wide activity feed beside
 * the task list, so the wrapper also supplies the flat member `list` (actor /
 * referenced names) + the team `timezone` (day bucketing). The feed makes its
 * own `useNodeHistory` fetch off the project root; the wrapper only threads the
 * resolving inputs.
 */
function HomeDashboardData({ teamId }: { teamId: string | undefined }) {
  const nodesQuery = useTaskNodes(teamId);
  const { byId: membersById, list: members } = useTaskTeamMembers(teamId);
  const teamQuery = useTeam(teamId);
  // Round-4 item ④ (#1043) — the dashboard's personal slice. `useCurrentMember`
  // resolves the signed-in viewer's membership_id on this team (null when
  // anonymous / off-team — the dashboard then renders the slice's calm empty
  // state). `useNotifications` is the same cheap unread-count fetch the
  // toolbar bell uses (`full: false` → `unread_only=true&limit=1`, 404-dark
  // when the notifications layer is off, returns 0 in that case); we surface
  // the count as a calm chip linking to the Taskmap where the bell lives.
  const me = useCurrentMember(teamId);
  const { unreadCount: mentionsUnread } = useNotifications(teamId, true, { full: false });
  const flat = Array.isArray(nodesQuery.data) ? nodesQuery.data : [];
  const roots = useMemo(() => buildTree(flat), [flat]);
  return (
    <HomeDashboard
      teamId={teamId}
      roots={roots}
      membersById={membersById}
      members={members}
      teamTimezone={teamQuery.data?.timezone}
      isLoading={nodesQuery.isLoading}
      currentMembershipId={me.membershipId}
      mentionsUnread={mentionsUnread}
    />
  );
}

/**
 * Members page for a tasks team. Rendered at `/teams/:teamId/map/members`.
 * Phase 2 (#762 Settings) replaces the Phase-0 stub with the real B6.4
 * members surface (`<MapMembers/>`). The export name stays stable for the
 * router + the #753 shell tests; the body now lives in `members/MapMembers`.
 *
 * #1043 (IA shell): this page is now reached from the shared Harbour topbar's
 * "Team" destination — OUTSIDE the tasks app's `Home · Taskmap` secondary nav.
 * So it does NOT mount `<MapNav/>` (the secondary nav is the Taskmap window's
 * own chrome). Legacy is unchanged either way (MapNav was editorial-only).
 */
export function MapMembersPage() {
  return (
    <MapThemeProvider>
      <MapMembers />
    </MapThemeProvider>
  );
}

/**
 * Settings page for a tasks team. Rendered at `/teams/:teamId/map/settings`.
 * Phase 2 (#762 Settings) replaces the Phase-0 stub with the real B6.1
 * settings home (`<MapSettingsHome/>`), which hosts B6.3 (optional features)
 * via a dialog and links to B6.4 (members). The export name stays stable for
 * the router + the #753 shell tests.
 *
 * #1043 (IA shell): reached from the Harbour topbar's "Settings" destination,
 * OUTSIDE the `Home · Taskmap` secondary nav — so no `<MapNav/>` here.
 */
export function MapSettingsPage() {
  return (
    <MapThemeProvider>
      <MapSettingsHome />
    </MapThemeProvider>
  );
}

/**
 * `<MapHomeRedirect/>` — the stale-bookmark safety redirect for the retired
 * `/teams/:teamId/map/home` route (PR-D, #1043, founder F5, 2026-05-26).
 *
 * Before PR-D, Home lived at `/map/home`; the default-flip made it the bare
 * `/map`. Any stale bookmark / link to `/map/home` lands here and is replaced
 * with the absolute Home path. An ABSOLUTE redirect (built from the `:teamId`
 * param) — not a relative `to=".."` — is used deliberately: React Router v6
 * resolves `..` relative to the whole flat route pattern, so from
 * `/teams/:teamId/map/home` a relative `..` would strip back to `/`, not to
 * `/map`. The absolute form is route-shape-independent and correct.
 */
export function MapHomeRedirect() {
  const { teamId } = useParams<{ teamId: string }>();
  return <Navigate to={teamId ? `/teams/${teamId}/map` : "/"} replace />;
}

/**
 * History is no longer a standalone page/route. #1043 folded it onto the Home
 * dashboard as a project-wide activity feed beside the task list
 * (`<ProjectActivityFeed/>` in `<HomeDashboard/>`); per-node history lives in
 * the node drawer's History tab. (PR-C briefly docked it as a right-side rail in
 * `<MapEditor/>`; round-3, 2026-05-27, removed that rail and relocated the feed
 * to the dashboard.) The `MapHistoryPage` export + the `/map/history` route +
 * the `MapHistory` wrapper + the `MapHistoryView`/`MapHistoryTaskPicker`
 * components were retired.
 */
